import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { playBeep, startTitleBlink } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, ImagePlus, Smile, Loader2, MessagesSquare, Settings } from "lucide-react";


function StickerManager({ stickers, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState((stickers || []).join("\n"));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText((stickers || []).join("\n")); }, [stickers]);

  const save = async () => {
    setSaving(true);
    try {
      const list = text.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/chat/stickers", { stickers: list });
      toast.success("Stiker disimpan");
      onSaved(list);
      setOpen(false);
    } catch (e) { toast.error("Gagal menyimpan stiker"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="manage-stickers-btn"><Settings className="w-3.5 h-3.5 mr-1" /> Kelola Stiker</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Kelola Stiker</DialogTitle>
          <DialogDescription>Tempel URL gambar stiker, satu per baris.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500">Tempel URL gambar stiker, satu per baris (PNG transparan disarankan).</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-40 rounded-xl" placeholder="https://.../stiker1.png&#10;https://.../stiker2.png" data-testid="stickers-textarea" />
        {stickers?.length > 0 && (
          <div className="grid grid-cols-6 gap-2 max-h-32 overflow-auto">
            {stickers.map((s, i) => <img key={i} src={s} alt="" className="w-12 h-12 object-contain" />)}
          </div>
        )}
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white" data-testid="save-stickers-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Stiker"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminChat({ heightClass = "h-[520px]", hideHeader = false }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // user_id
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [stickers, setStickers] = useState([]);
  const [showStk, setShowStk] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const listRef = useRef(null);
  const atBottomRef = useRef(true);
  const activeRef = useRef(null);
  const prevUnread = useRef(0);

  const imgUrl = (id) => `${api.defaults.baseURL}/files/${id}`;

  useEffect(() => { api.get("/chat/stickers").then((r) => setStickers(r.data?.stickers || [])).catch(() => {}); }, []);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get("/admin/chat/conversations");
      setConvs(r.data);
      const totalUnread = r.data.reduce((s, c) => s + (c.unread || 0), 0);
      if (totalUnread > prevUnread.current) {
        playBeep();
        startTitleBlink("💬 Pesan baru dari konsumen!");
        toast.info("Pesan chat baru masuk dari konsumen");
      }
      prevUnread.current = totalUnread;
    } catch (e) {}
  }, []);

  const loadMsgs = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await api.get(`/admin/chat/conversations/${cid}`);
      setMsgs((prev) => {
        const next = r.data || [];
        if (prev.length === next.length && prev[prev.length - 1]?.id === next[next.length - 1]?.id) return prev;
        return next;
      });
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadConvs();
    const t = setInterval(() => {
      loadConvs();
      if (activeRef.current) loadMsgs(activeRef.current);
    }, 4000);
    return () => clearInterval(t);
  }, [loadConvs, loadMsgs]);

  useEffect(() => { activeRef.current = active; if (active) { atBottomRef.current = true; loadMsgs(active); loadConvs(); } }, [active, loadMsgs, loadConvs]);
  useEffect(() => { if (atBottomRef.current) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const onListScroll = (e) => {
    const el = e.currentTarget;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const send = async (body) => {
    const r = await api.post("/chat/messages", { user_id: active, ...body });
    atBottomRef.current = true;
    setMsgs((m) => [...m, r.data]);
    loadConvs();
  };
  const sendText = async () => { if (!text.trim() || !active) return; const t = text; setText(""); await send({ text: t }); };
  const sendImage = async (e) => {
    const f = e.target.files?.[0]; if (!f || !active) return;
    const fd = new FormData(); fd.append("file", f);
    const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await send({ image_file_id: up.data.file_id });
    if (fileRef.current) fileRef.current.value = "";
  };
  const sendSticker = async (s) => { setShowStk(false); await send({ sticker: s }); };

  const activeConv = convs.find((c) => c.user_id === active);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-chat">
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2"><MessagesSquare className="w-5 h-5 text-[#F0005A]" /><h3 className="font-heading font-semibold text-[#00133F]">Chat Konsumen</h3></div>
          <StickerManager stickers={stickers} onSaved={setStickers} />
        </div>
      )}
      <div className={`grid md:grid-cols-3 min-h-0 ${heightClass}`}>
        {/* Conversation list */}
        <div className="border-r border-slate-100 overflow-y-auto" data-testid="chat-conversation-list">
          {convs.length === 0 && <p className="text-center text-sm text-slate-400 mt-8 px-4">Belum ada percakapan.</p>}
          {convs.map((c) => (
            <button key={c.user_id} onClick={() => setActive(c.user_id)} data-testid={`conversation-${c.user_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${active === c.user_id ? "bg-[#F0005A]/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm text-[#00133F] truncate">{c.name || "Konsumen"}</span>
                {c.unread > 0 && <span className="bg-[#F0005A] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center" data-testid={`conv-unread-${c.user_id}`}>{c.unread}</span>}
              </div>
              <p className="text-xs text-slate-400 truncate mt-0.5">
                {c.last ? (c.last.text || (c.last.sticker ? "🖼️ Stiker" : c.last.image_file_id ? "📷 Gambar" : "")) : "—"}
              </p>
            </button>
          ))}
        </div>

        {/* Message pane */}
        <div className="md:col-span-2 flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">Pilih percakapan untuk membalas</div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                <p className="font-medium text-sm text-[#00133F]">{activeConv?.name || "Konsumen"}</p>
                <p className="text-xs text-slate-400">{activeConv?.email}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/50" data-testid="admin-chat-messages" ref={listRef} onScroll={onListScroll}>
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "admin" ? "bg-[#00133F] text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                      {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                      {m.image_file_id && <a href={imgUrl(m.image_file_id)} target="_blank" rel="noreferrer"><img src={imgUrl(m.image_file_id)} alt="" className="rounded-lg max-w-full mt-1" /></a>}
                      {m.sticker && <img src={m.sticker} alt="stiker" className="w-20 h-20 object-contain" />}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              {showStk && stickers.length > 0 && (
                <div className="border-t border-slate-100 p-2 grid grid-cols-6 gap-2 max-h-28 overflow-y-auto">
                  {stickers.map((s, i) => <button key={i} onClick={() => sendSticker(s)}><img src={s} alt="" className="w-12 h-12 object-contain hover:scale-110 transition-transform" /></button>)}
                </div>
              )}
              <div className="border-t border-slate-100 p-2 flex items-center gap-1.5">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="admin-chat-image-input" />
                <button onClick={() => fileRef.current?.click()} className="p-2 text-slate-500 hover:text-[#F0005A]"><ImagePlus className="w-5 h-5" /></button>
                {stickers.length > 0 && <button onClick={() => setShowStk((v) => !v)} className="p-2 text-slate-500 hover:text-[#F0005A]"><Smile className="w-5 h-5" /></button>}
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
                  placeholder="Tulis balasan..." className="flex-1 text-sm rounded-full" data-testid="admin-chat-input" />
                <button onClick={sendText} className="p-2 bg-[#F0005A] text-white rounded-full" data-testid="admin-chat-send-btn"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
